import React from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'

export interface IntentStepProps {
  albumTitle: string
  photoCount: number
  coverUrl?: string
}

const Hero = styled(Box)({
  borderRadius: 20,
  overflow: 'hidden',
  aspectRatio: '21 / 9',
  maxHeight: 220,
  background: 'var(--ds-hover)',
  marginBottom: 'var(--ds-space-6)',
  position: 'relative',
  '& img': { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
})

const HeroFade = styled(Box)({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(180deg, transparent 45%, rgba(0,0,0,0.35) 100%)',
  pointerEvents: 'none',
})

const FeatureGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
  '@media (min-width: 560px)': {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
})

const Feature = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: '14px 14px 16px',
  borderRadius: 16,
  background: 'var(--ds-hover)',
  border: '1px solid var(--ds-border)',
  minHeight: 96,
})

const Num = styled(Box)({
  width: 22,
  height: 22,
  borderRadius: 'var(--ds-radius-pill)',
  background: 'var(--ds-ink)',
  color: 'var(--ds-inverse-ink)',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.02em',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const FEATURES = [
  { n: '1', label: 'Scene', detail: 'Pick a room' },
  { n: '2', label: 'Style', detail: 'Frame · color · material' },
  { n: '3', label: 'Prints', detail: 'Place your photos' },
  { n: '4', label: 'Share', detail: 'Link + QR' },
] as const

export function IntentStep({ albumTitle, photoCount, coverUrl }: IntentStepProps) {
  return (
    <Box maxWidth={640}>
      {coverUrl && (
        <Hero>
          <img src={coverUrl} alt='' />
          <HeroFade />
        </Hero>
      )}

      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--ds-ink-subtle)',
          mb: 1.5,
        }}
      >
        3D Gallery
      </Typography>

      <Typography
        component='h2'
        sx={{
          fontSize: { xs: 28, md: 34 },
          fontWeight: 700,
          letterSpacing: '-0.035em',
          lineHeight: 1.1,
          mb: 1.5,
        }}
      >
        Make {albumTitle} walkable
      </Typography>

      <Typography
        sx={{
          fontSize: 15,
          color: 'var(--ds-ink-muted)',
          lineHeight: 1.5,
          mb: 4,
          maxWidth: 480,
        }}
      >
        Turn your {photoCount} photo{photoCount === 1 ? '' : 's'} into a custom room — frames,
        mats, and materials. On create, photos are placed on the walls automatically, then you
        can fine-tune in the editor.
      </Typography>

      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--ds-ink-subtle)',
          mb: 1.5,
        }}
      >
        Four quick steps
      </Typography>

      <FeatureGrid>
        {FEATURES.map((f) => (
          <Feature key={f.n}>
            <Num>{f.n}</Num>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                {f.label}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'var(--ds-ink-muted)', mt: 0.4, lineHeight: 1.35 }}>
                {f.detail}
              </Typography>
            </Box>
          </Feature>
        ))}
      </FeatureGrid>
    </Box>
  )
}
